import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  params, string, arrayOf, number, customType, useLazyDataModel, extend
} from '@thd-nucleus/data-sources';
import {
  TextField,
  Typography,
  LoadingButton
} from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { validateField } from '../helpers';
import { PROJECT_NAME_KEY } from '../constants';

export const InlineProjectCreation = ({
  products,
  singleProduct,
  multipleProduct,
  onSuccess,
  onError,
}) => {
  const [projectName, setProjectName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const [pendingProjectId, setPendingProjectId] = useState(null);
  const pendingProjectNameRef = useRef('');

  const { svocID } = useThdCustomer();

  const [createProject] = useLazyDataModel('createProject', {
    context: { withAuth: true },
    fetchPolicy: 'no-cache'
  });

  const [fetchProjectDetails, { data: projectDetailsData }] = useLazyDataModel('projectDetails', {
    context: { withAuth: true },
    fetchPolicy: 'no-cache'
  });

  const [addItem] = useLazyDataModel('updateMaterials', {
    context: { withAuth: true },
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    const addItemsToProject = async () => {
      const { defaultListId, defaultListMutationKey } = projectDetailsData?.projectDetails || {};

      if (!pendingProjectId || !defaultListId || !defaultListMutationKey) {
        return;
      }

      const isMultiAdd = products?.length > 1;
      const items = isMultiAdd ? multipleProduct : singleProduct;

      if (!items || items.length === 0) {
        if (onSuccess) {
          onSuccess({
            projectId: pendingProjectId,
            projectName: pendingProjectNameRef.current,
          });
        }
        setIsCreating(false);
        setPendingProjectId(null);
        return;
      }

      try {
        const addResponse = await addItem({
          variables: {
            listId: defaultListId,
            mutationKey: defaultListMutationKey,
            operation: 'ADD_ITEM',
            groupName: 'Unassigned',
            items
          }
        });

        if (addResponse?.data?.updateMaterials?.groupId) {
          if (onSuccess) {
            onSuccess({
              projectId: pendingProjectId,
              projectName: pendingProjectNameRef.current,
              itemCount: products.length,
              items: products
            });
          }
        } else {
          const errorMsg = 'Failed to add item to project. Please try again.';
          setError(errorMsg);
          if (onError) onError(errorMsg);
        }
      } catch (err) {
        const errorMsg = 'Something went wrong. Please try again.';
        setError(errorMsg);
        if (onError) onError(err);
      }

      setIsCreating(false);
      setPendingProjectId(null);
    };

    addItemsToProject();
  }, [projectDetailsData]);

  const handleNameChange = (event) => {
    const { value } = event.target;
    setProjectName(value);
    if (error) {
      const { isError, errorMessage } = validateField({ value, key: PROJECT_NAME_KEY });
      setError(isError ? errorMessage : '');
    }
  };

  const handleNameValidation = () => {
    const { isError, errorMessage } = validateField({ value: projectName, key: PROJECT_NAME_KEY });
    setError(isError ? errorMessage : '');
  };

  const handleCreateProject = async () => {
    const { isError, errorMessage } = validateField({ value: projectName, key: PROJECT_NAME_KEY });
    if (isError) {
      setError(errorMessage);
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      const projectResponse = await createProject({
        variables: {
          accountId: svocID,
          projectName: projectName.trim()
        }
      });

      const createdProjectData = projectResponse?.data?.createProject;

      if (createdProjectData?.id) {
        const { id, projectName: newProjectName } = createdProjectData;
        pendingProjectNameRef.current = newProjectName || projectName.trim();
        setPendingProjectId(id);
        fetchProjectDetails({ variables: { projectId: id } });
      } else {
        setError('Failed to create project. Please try again.');
        setIsCreating(false);
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      if (onError) onError(err);
      setIsCreating(false);
    }
  };

  return (
    <>
      <div className="sui-mb-3">
        <Typography variant="h5" weight="bold">
          Create a New Project
        </Typography>
      </div>

      <div className="sui-grid sui-grid-cols-5 sui-gap-2 sui-items-start sui-pr-4">
        {/* eslint-disable-next-line max-len */}
        <div className="sui-col-span-3 sui-min-w-0 [&_.sui-input-base-root]:sui-rounded-lg [&_.sui-input-base-outline]:sui-rounded-lg">
          <TextField
            id="project-name-input"
            name="projectName"
            placeholder="Project Name"
            value={projectName}
            onChange={handleNameChange}
            onBlur={handleNameValidation}
            fullWidth
            statusMessage={error || undefined}
            status={error ? 'error' : undefined}
            disabled={isCreating}
          />
        </div>
        <div className="sui-col-span-2 sui-pt-2 [&_button]:sui-rounded-lg">
          <LoadingButton
            fullWidth
            variant="primary"
            onClick={handleCreateProject}
            disabled={!projectName.trim() || isCreating}
            data-testid="inline-create-project-button"
            loading={isCreating}
          >
            Create
          </LoadingButton>
        </div>
      </div>
    </>
  );
};

InlineProjectCreation.displayName = 'InlineProjectCreation';

InlineProjectCreation.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    sku: PropTypes.string
  })),
  singleProduct: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    sku: PropTypes.string,
    itemType: PropTypes.string
  })),
  multipleProduct: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    sku: PropTypes.string,
    itemType: PropTypes.string
  })),
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
};

InlineProjectCreation.defaultProps = {
  products: [],
  singleProduct: [],
  multipleProduct: [],
  onSuccess: () => {},
  onError: () => {},
};

InlineProjectCreation.dataModel = extend(
  {
    createProject: params({
      accountId: string().isRequired(),
      projectName: string().isRequired(),
    }).mutation().shape({
      id: string(),
      projectName: string()
    })
  },
  {
    projectDetails: params({ projectId: string().isRequired() }).shape({
      id: string(),
      defaultListId: string(),
      defaultListMutationKey: string(),
      projectName: string(),
    })
  },
  {
    updateMaterials: params({
      listId: string().isRequired(),
      mutationKey: string().isRequired(),
      operation: customType('MaterialsOperationType').enum(
        ['EDIT_GROUP_NAME', 'DELETE_GROUP', 'ADD_ITEM', 'DELETE_ITEM', 'UPDATE_QUANTITY', 'REARRANGE']
      ),
      groupName: string(),
      groupId: arrayOf(string()),
      items: arrayOf(customType('Items').shape({
        itemId: string(),
        id: string(),
        omsId: string(),
        quantity: number(),
        sku: string(),
        itemType: string()
      }))
    }).mutation().shape({
      groupId: string()
    })
  }
);
